<template>
  <BaseModal
    v-if="campaign && !loading"
    ref="modal"
    tabbed
    modal-classes="max-w-2xl mx-auto"
    @modal-after-leave="doCloseAction"
  >
    <template #tabs>
      <div
        class="modal__tab-item"
        :class="{ active: currentTab === getTab('general') }"
        @click="currentTab = getTab('general')"
      >
        General
      </div>
      <div
        class="modal__tab-item"
        :class="{ active: currentTab === getTab('schedule') }"
        @click="currentTab = getTab('schedule')"
      >
        Schedule
      </div>
      <div
        class="modal__tab-item"
        :class="{ active: currentTab === getTab('pricing') }"
        @click="currentTab = getTab('pricing')"
      >
        Pricing
      </div>
      <div
        class="modal__tab-item"
        :class="{ active: currentTab === getTab('costing') }"
        @click="currentTab = getTab('costing')"
      >
        Cost Estimation
      </div>
      <div
        class="modal__tab-item"
        :class="{ active: currentTab === getTab('channels') }"
        @click="currentTab = getTab('channels')"
      >
        Channels
      </div>
    </template>

    <template #header="modalProps">
      <div class="flex items-top">
        <div class="flex-1">
          <span class="text-xs text-gray-700 uppercase tracking-wider">Τροποποιηση</span>
          <h6 class="heading">
            Ad Plan
            <router-link
              v-if="projectClient && !$route.params.projectId"
              :to="{
                name: 'accounts.projects.project.campaigns',
                params: {
                  accountId: projectClient.id,
                  projectId: project.uuid,
                },
              }"
            >
              {{ campaign.name }}
            </router-link>
            <template v-else-if="campaign.name">
              {{ campaign.name }}
            </template>
          </h6>
          <BaseBadge green v-if="campaign.totalActiveChannels > 0" class="mt-2"> Active </BaseBadge>
        </div>
        <div class="flex-none">
          <BaseButton ghost type="button" @click="modalProps.close" :disabled="submitting">
            Close
          </BaseButton>
        </div>
      </div>
    </template>

    <template>
      <div class="flex">
        <div class="w-full">
          <component :is="currentTab" v-bind="{ campaign }" />
        </div>
      </div>
    </template>
  </BaseModal>

  <BaseModal
    v-else
    ref="modal"
    tabbed
    modal-classes="max-w-2xl mx-auto"
    @modal-after-leave="doCloseAction"
  >
    <template #header> Loading ... </template>

    <div class="flex h-full">
      <Loader class="m-auto" />
    </div>
  </BaseModal>
</template>

<script>
import { omit } from 'lodash-es';
import { mapActions, mapMutations, mapState } from 'vuex';
import GeneralTab from './campaignModal/TabGeneral.vue';
import ScheduleTab from './campaignModal/TabSchedule.vue';
import TargetingTab from './campaignModal/TabTargeting.vue';
import CostingTab from './campaignModal/TabCosting.vue';
import ChannelsTab from './campaignModal/TabChannels.vue';

const tabs = {
  default: 'GeneralTab',
  schedule: 'ScheduleTab',
  pricing: 'TargetingTab',
  costing: 'CostingTab',
  channels: 'ChannelsTab',
};

export default {
  name: 'CampaignModal',

  components: {
    GeneralTab,
    ScheduleTab,
    TargetingTab,
    CostingTab,
    ChannelsTab,
  },

  props: {
    campaignId: {
      type: String,
      required: true,
    },
    closeOnSuccess: Boolean,
  },

  data() {
    const { tab = 'default' } = this.$route.query;

    return {
      currentTab: this.getTab(tab),
      submitting: false,
      scheduleData: null,
      targetingData: null,
      costingData: null,
      loading: false,
    };
  },

  computed: {
    ...mapState('entities', {
      campaign({ campaigns }) {
        return campaigns.byId[this.campaignId] || null;
      },
      project({ projects }) {
        if (!this.campaign || !this.campaign.projectUuid) return null;
        return projects.byId[this.campaign.projectUuid] || null;
      },
      projectAccounts({ accounts }) {
        if (!this.project || !this.project.accounts) return [];
        return this.project.accounts.map((id) => accounts.byId[id]);
      },
    }),
    projectClient() {
      return this.projectAccounts.find((account) => account.type === 'client') || null;
    },
  },

  created() {
    this.doInitAction();
  },

  destroyed() {
    this.doCleanupAction();
  },

  methods: {
    ...mapActions('ui/campaigns', {
      readCampaign: 'getCampaign',
    }),
    ...mapMutations('ui/campaigns', ['setCurrent']),
    getTab(name) {
      return tabs[name] || tabs.default;
    },
    async doInitAction() {
      try {
        this.loading = true;

        await this.readCampaign({
          campaignId: this.campaignId,
        });

        const {
          dateStart = null,
          dateEnd = null,
          target = null,
          result = null,
          pricePerResult = null,
          price = null,
          fixedCost = null,
        } = this.campaign || {};

        this.scheduleData = {
          dateStart,
          dateEnd,
        };

        this.targetingData = {
          target,
          result,
          pricePerResult,
          price,
        };

        this.costingData = {
          fixedCost,
        };
      } catch (error) {
        this.doCloseAction();
      } finally {
        this.loading = false;
      }
    },

    doCloseAction() {
      const {
        name,
        params,
        query: { editCampaign, ...query },
      } = this.$route;

      if (editCampaign) {
        this.$router.replace({
          name,
          params,
          query: omit(query, ['tab']),
        });
      }

      this.$emit('campaign-modal-close');
    },

    doCleanupAction() {
      this.setCurrent(null);
    },
  },
};
</script>
